import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse, OrderSampleJob, OrderSampleStore } from '@/types';

/** `attachment; filename="x.xlsx"` → x.xlsx. The header is only readable when CORS exposes it. */
export const fileNameFromDisposition = (header: unknown): string | null => {
  const match = /filename="?([^";]+)"?/i.exec(String(header ?? ''));
  return match ? match[1] : null;
};

/** The name machine gives the file, for when its header can't be read. */
export const fallbackFileName = (job: Pick<OrderSampleJob, 'shop'>) => `${job.shop}_test_orders.xlsx`;

/**
 * A blob download that fails carries machine's JSON message as a Blob too, so the axios
 * interceptor can't read it. Turn it back into a readable error.
 */
const readBlobError = async (error: unknown): Promise<Error> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return error instanceof Error ? error : new Error(String(error));
  const text = await data.text();
  try {
    const parsed = JSON.parse(text) as { message?: string };
    return new Error(parsed.message || "Couldn't download the sheet.");
  } catch (parseError) {
    console.error('order-samples.download: error body is not JSON', parseError);
    return new Error("Couldn't download the sheet.");
  }
};

export const orderSamplesService = {
  /** Each brand's Shopify store, with the latest sheet built for it. */
  async stores(accountId: number): Promise<OrderSampleStore[]> {
    const res = await api.get<ApiResponse<{ stores: OrderSampleStore[] }>>(
      API_ENDPOINTS.ORDER_SAMPLES.STORES(accountId)
    );
    return res.data.data.stores;
  },

  /** Starts a sheet; machine answers at once with the queued job and builds it in the background. */
  async start(accountId: number, storeId: number): Promise<OrderSampleJob> {
    const res = await api.post<ApiResponse<{ job: OrderSampleJob }>>(
      API_ENDPOINTS.ORDER_SAMPLES.START(accountId),
      { storeId }
    );
    return res.data.data.job;
  },

  async job(accountId: number, jobId: string): Promise<OrderSampleJob> {
    const res = await api.get<ApiResponse<{ job: OrderSampleJob }>>(
      API_ENDPOINTS.ORDER_SAMPLES.JOB(accountId, jobId)
    );
    return res.data.data.job;
  },

  /** The finished sheet as a file, with the name to save it under. */
  async download(accountId: number, job: OrderSampleJob): Promise<{ blob: Blob; fileName: string }> {
    try {
      const res = await api.get<Blob>(API_ENDPOINTS.ORDER_SAMPLES.FILE(accountId, job.jobId), {
        responseType: 'blob'
      });
      const fileName = fileNameFromDisposition(res.headers?.['content-disposition']) ?? fallbackFileName(job);
      return { blob: res.data, fileName };
    } catch (error) {
      throw await readBlobError(error);
    }
  }
};

/** Hands a blob to the browser as a download. */
export const saveFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
